// The mapping target vocabulary and the consent-resolution rule — a FAITHFUL
// transcription of the import-engine annex (§4.1b, §4.2) and SPEC §5.4.2 /
// §9.1 / §9.3.
//
// Sources:
//   docs/initiatives/new/dashboard-import-tool/import-engine-contract.md
//     §4.1b (SourceFileHint, ResolvedConsent, the normative consent precedence)
//     §4.2  (the P2 MappingProvider port; the field catalog paragraph at its end)
//   docs/initiatives/new/dashboard-import-tool/SPEC.md
//     §5.4.2 ("Rejected mapping targets")
//     §9.1 / §9.3 (consent capture, the canonical vocabulary, precedence)
//
// WHY THIS FILE IS A LEAF. Annex §4.2 says the field catalog "is published in
// `contracts/imports` as the single source both the provider and the validator
// read." Both sides of that sentence — the P2 mapping provider and the §5.4
// validator — plus O3's `ColumnMapping` and P2's `ColumnProposal` need
// `ColumnTarget`. So this module imports nothing from `./import-job.js` or
// `./mapping.js`; those files import IT. Adding an import back the other way
// would create a cycle in the contract package's own graph.
//
// LIKE THE REST OF THIS PACKAGE, THIS IS A TRANSCRIPTION, NOT A DESIGN. No
// invented catalog members, no invented consent states. If a shape here looks
// wrong, the fix is a SPEC/annex edit first (the plan-is-wrong protocol), then
// a transcription of the change.

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Annex §4.2 — the V1 field catalog (`TargetFieldKey`)
// ---------------------------------------------------------------------------

/**
 * Annex §4.2, final paragraph: "**The field catalog** (`TargetFieldKey`) is
 * published in `contracts/imports` as the single source both the provider and
 * the validator read. V1 membership: `email`, `firstName`, `lastName`,
 * `fullName`, `organizationName`, `phone`, `addressLine1`, `addressLine2`,
 * `city`, `state`, `postalCode`, `country`, `externalId`, `emailConsent`,
 * `smsConsent`, plus the `CUSTOM_FIELD` / `TAG` / `SKIP` sentinels."
 *
 * `organizationName` is a catalog member because a file that HAS an Organization
 * column needs somewhere to put it — it is stored on the contact and merged like
 * any other field. It used to travel as a `CUSTOM_FIELD` keyed
 * `organizationName`, special-cased by the validation engine and the writer;
 * that bridge is still READ (saved mapping configs depend on it) but is no
 * longer how a new mapping expresses it.
 *
 * ⚠️ It is NOT an identity field, as of 2026-08-25. This comment used to say
 * "`organizationName` is an IDENTITY field... SPEC §5.4.1 admits a row whose
 * only name is an organization's" — that was true until `D-PERSON-NAME-REQUIRED`
 * made firstName AND lastName mandatory and stopped admissibility from reading
 * `organizationName` at all. An organization-only row is now EXCLUDED
 * (`NO_USABLE_NAME`), so this member confers storage, never admissibility. The
 * membership itself did NOT change — do not "tidy" it out on the strength of the
 * correction; see `dataset.ts`'s `NO_USABLE_NAME` comment and
 * `docs/initiatives/new/dashboard-import-tool/decisions.md#D-PERSON-NAME-REQUIRED`.
 *
 * Member order is the annex's own order, so the two can be diffed line by line.
 *
 * ⚠️ **V2 donation fields are DELIBERATELY EXCLUDED** — a V1 mapping cannot
 * target a transaction field (INV-1 scope). This is not an oversight and it is
 * not a "V1 subset we'll widen casually": the absence is what makes SPEC §3.4's
 * "merge never touches donation data" true at the type level rather than by
 * convention. `totalTransactionCount` and friends are not merely missing from
 * this list, they are actively rejected — see `REJECTED_MAPPING_TARGETS` below.
 * Adding a donation field here would silently reopen that hole for every
 * consumer, because the catalog is what the mapping UI offers AND what the
 * backend accepts.
 *
 * Note the catalog uses the WIZARD's field names (`state`, `postalCode`), which
 * are not the contacts writer's stored address field names (`region`, `postal`
 * — SPEC §5.4.3). The rename happens in the write path, not here.
 */
export const TARGET_FIELD_KEYS = [
  'email',
  'firstName',
  'lastName',
  'fullName',
  'organizationName',
  'phone',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'postalCode',
  'country',
  'externalId',
  'emailConsent',
  'smsConsent',
] as const;

export const targetFieldKeySchema = z.enum(TARGET_FIELD_KEYS);
export type TargetFieldKey = z.infer<typeof targetFieldKeySchema>;

// ---------------------------------------------------------------------------
// Annex §4.2 — the three routing sentinels
// ---------------------------------------------------------------------------

/**
 * The three routing sentinels from annex §4.2. They are not fields: each names
 * a destination *other than* a catalog field —
 *
 *   - `CUSTOM_FIELD` — write the column to a per-org custom field (SPEC §7.1)
 *   - `TAG`          — turn the column's values into contact tags
 *   - `SKIP`         — deliberately drop the column
 *
 * They live in the same union as the catalog because annex §4.2 obligation 1
 * ("total over headers") requires every header to carry a decision, and "this
 * column goes nowhere" is a decision. `SKIP` in particular is the difference
 * between an explicitly-discarded column and a forgotten one.
 *
 * Obligation 4 is why `CUSTOM_FIELD` matters more than it looks: "Genuinely
 * ambiguous columns route to `CUSTOM_FIELD`, never to a false-confidence
 * consent value." The sentinel is the designated escape hatch, so removing it
 * would push ambiguity onto `emailConsent`/`smsConsent` — the exact ImportCSV
 * #15 failure mode the obligation guards against.
 */
export const MAPPING_SENTINELS = ['CUSTOM_FIELD', 'TAG', 'SKIP'] as const;

export const mappingSentinelSchema = z.enum(MAPPING_SENTINELS);
export type MappingSentinel = z.infer<typeof mappingSentinelSchema>;

/**
 * `TargetFieldKey | "CUSTOM_FIELD" | "TAG" | "SKIP"` — the type annex §4.2 uses
 * in BOTH O3's `ColumnMapping` (the durable, human-confirmed config) and P2's
 * `ColumnProposal` (the disposable machine proposal). One union serves both so
 * a proposal's target is, structurally, already a legal confirmed target; a
 * second parallel enum would let the two drift and make "confirm this proposal"
 * a lossy conversion.
 *
 * Annex §4.2 obligation 2, **forced choice**: "`target` is a catalog member or
 * one of the three routing sentinels. **Never free text.** An unrecognized
 * value is a contract violation and is coerced to `null`."
 *
 * Two consequences for consumers:
 *
 *  1. Do NOT widen this to `z.string()` anywhere, and do NOT `.catch()` an
 *     arbitrary string into it. A free-text target is how a provider swap
 *     smuggles a new write destination past review.
 *  2. `null` is the representation of "unrecognized / cannot judge" — so a
 *     nullable-target field belongs on the *proposal* schema (obligation 1: a
 *     provider that cannot judge returns `target: null, confidence: 0`), NOT
 *     here. This schema is the non-null vocabulary; callers add `.nullable()`
 *     at the site where null is legal. That keeps O3 from accidentally
 *     accepting a null target it would then have to write somewhere.
 */
export const columnTargetSchema = z.union([targetFieldKeySchema, mappingSentinelSchema]);
export type ColumnTarget = z.infer<typeof columnTargetSchema>;

// ---------------------------------------------------------------------------
// SPEC §5.4.2 — rejected mapping targets
// ---------------------------------------------------------------------------

/**
 * SPEC §5.4.2, "**Rejected mapping targets.**": "These fields are writer-owned
 * and a column may never be mapped onto them: `totalTransactionCount`,
 * `totalTransactionValue`, `created_at`, `updated_at`, `id`, `merchant`,
 * `merchantID`, `emailMerchantKey`, `phoneMerchantKey`, `is_verified`. The
 * mapping UI does not offer them; the backend rejects them if sent. This is the
 * mechanical half of §3.4's 'merge never touches donation data.'"
 *
 * Both halves of that last sentence are load-bearing and they are enforced in
 * different places:
 *
 *  - **The mapping UI does not offer them.** Structurally true already —
 *    `TARGET_FIELD_KEYS` above does not contain them, so a conformant UI cannot
 *    render them as options.
 *  - **The backend REJECTS them if sent.** That is what this list is for. The
 *    catalog's absence is not sufficient on its own: a hand-crafted
 *    `confirmMapping` payload, a stale client, or a vendor wizard adapter
 *    (annex §4.3a) can put any string on the wire. `columnTargetSchema` would
 *    already refuse it, but a *named, cited* list lets the backend answer
 *    "why?" with the §5.4.2 reason ("writer-owned") instead of a generic enum
 *    error — and lets a validator check an incoming *source header* before any
 *    mapping exists at all.
 *
 * Two of these are worse than merely wrong to write. `merchantID`,
 * `emailMerchantKey`, and `phoneMerchantKey` are the contact identity/lookup
 * keys (SPEC §5.4.3): overwriting one does not corrupt a display value, it
 * makes a contact unfindable and unmergeable, and DynamoDB rejects empty
 * strings on those indexed attributes outright. `totalTransactionCount` /
 * `totalTransactionValue` are the donation rollups §3.4 promises import never
 * touches.
 */
export const REJECTED_MAPPING_TARGETS = [
  'totalTransactionCount',
  'totalTransactionValue',
  'created_at',
  'updated_at',
  'id',
  'merchant',
  'merchantID',
  'emailMerchantKey',
  'phoneMerchantKey',
  'is_verified',
] as const;

export type RejectedMappingTarget = (typeof REJECTED_MAPPING_TARGETS)[number];

/**
 * Whether a string names a writer-owned field that may never be a mapping
 * target (SPEC §5.4.2).
 *
 * **The comparison is deliberately CASE-INSENSITIVE.** The input to this guard
 * is user-controlled: a header out of an uploaded spreadsheet, or a `target`
 * string off the wire. A source file's header row could plausibly arrive as
 * `MerchantID`, `ID`, `Created_At`, or `IS_VERIFIED` — CRM exports title-case
 * and upper-case headers routinely, and nothing in the parse contract
 * normalizes case (annex §4.1 obligation 4: "Parse does not mutate values. No
 * trimming, no case folding"). A case-sensitive check is therefore trivially
 * bypassed by the casing of a file someone chose to upload, which makes it a
 * guard in name only. The guard exists to be **mechanical**, so it must not
 * depend on the attacker's/user's formatting choices.
 *
 * The exact-case spellings in `REJECTED_MAPPING_TARGETS` are still the SPEC's
 * and are kept verbatim (note the mixed conventions — `created_at` snake_case
 * next to `merchantID` camelCase — which are the writer's real field names, not
 * a style to normalize). Case is folded only for the *comparison*.
 *
 * Scope note: this checks case, not separators. `merchant_id` is NOT flagged,
 * because it is not the writer's field name — it would fail
 * `columnTargetSchema` as a target, and as a source *header* it is a perfectly
 * legal column to map onto `externalId`. Widening this to fuzzy matching would
 * start rejecting legitimate source columns.
 */
export function isRejectedMappingTarget(header: string): boolean {
  const normalized = header.trim().toLowerCase();
  return REJECTED_MAPPING_TARGETS.some((rejected) => rejected.toLowerCase() === normalized);
}

// ---------------------------------------------------------------------------
// Annex §4.1b — the per-file role signal (`SourceFileHint`)
// ---------------------------------------------------------------------------

/**
 * Annex §4.1b `fileRole`. WHY A PER-FILE SIGNAL EXISTS AT ALL: "Mailchimp
 * encodes consent state in *which status file a row came from* (`subscribed` /
 * `unsubscribed` / `cleaned` / `pending`), not in any column. No column in any
 * Mailchimp export ever fingerprints to a consent field, correctly so."
 * Column-level mapping cannot close that gap regardless of which mapping engine
 * wins, so the upload step carries the role.
 *
 * SPEC §9.3 states the consequence plainly: the file role "is a consent input
 * of **equal standing** to a column." It is not a weaker hint.
 *
 * `UNKNOWN` is a real member, distinct from the field being absent: absent
 * means no role was ever determined, `UNKNOWN` means something looked and could
 * not tell. Neither carries consent information (see
 * `EMAIL_CONSENT_BY_FILE_ROLE`), but only one of them means a heuristic ran.
 */
export const SOURCE_FILE_ROLES = [
  'SUBSCRIBED',
  'UNSUBSCRIBED',
  'CLEANED',
  'PENDING',
  'ALL_CONTACTS',
  'UNKNOWN',
] as const;

export const sourceFileRoleSchema = z.enum(SOURCE_FILE_ROLES);
export type SourceFileRole = z.infer<typeof sourceFileRoleSchema>;

/**
 * Annex §4.1b `declaredBy` — HOW the role was determined, kept alongside the
 * role itself because the three are not equally trustworthy and the resolution
 * rule below deliberately does not weigh them.
 *
 *   - `FILENAME_HEURISTIC` — inferred from the filename (weakest; a user can
 *     rename `unsubscribed.csv` to anything)
 *   - `FINGERPRINT` — inferred from the file's column signature
 *   - `USER` — the actor declared it in the upload step
 *
 * Recorded, not consulted, on purpose: precedence rule 1 resolves toward the
 * more restrictive value regardless of provenance, so a weak filename heuristic
 * saying `UNSUBSCRIBED` still suppresses. Downgrading a suppression because we
 * were only "fairly sure" would be resolving a conflict toward permission,
 * which §4.1b forbids. `declaredBy` is for the audit trail and for the UI to
 * decide what to ask the user to confirm.
 */
export const SOURCE_FILE_HINT_DECLARED_BY = ['FILENAME_HEURISTIC', 'FINGERPRINT', 'USER'] as const;

export const sourceFileHintDeclaredBySchema = z.enum(SOURCE_FILE_HINT_DECLARED_BY);
export type SourceFileHintDeclaredBy = z.infer<typeof sourceFileHintDeclaredBySchema>;

/**
 * Annex §4.1b:
 *
 *     SourceFileHint {
 *       platform?: KnownPlatform | null
 *       fileRole?: "SUBSCRIBED" | "UNSUBSCRIBED" | "CLEANED" | "PENDING" | "ALL_CONTACTS" | "UNKNOWN"
 *       declaredBy: "FILENAME_HEURISTIC" | "FINGERPRINT" | "USER"
 *     }
 *
 * `declaredBy` is the one required field: a hint that says nothing about where
 * it came from is not auditable, and §4.1b's whole purpose is a consent input
 * that survives into the results file.
 */
export const sourceFileHintSchema = z.object({
  /**
   * The source platform this file came from (Mailchimp, Givebutter, Donorbox…).
   *
   * ⚠️ The annex types this `KnownPlatform | null`, but **no canonical
   * membership list for `KnownPlatform` is published in the SPEC or the annex**
   * — `KnownPlatform` appears exactly twice in the annex (here and
   * `MappingProposal.detectedPlatform`) and is never defined, and the SPEC's
   * platform tables (§9.3, Appendix B.4) are explicitly "illustrative, not
   * exhaustive". So this is a `string` here rather than an invented enum:
   * fabricating the membership would make this file the de-facto source of a
   * list nobody ratified, and the mapping engine would then reject a real
   * platform the fingerprint corpus grows into during Milestone C.
   *
   * Narrowing `z.string()` to `z.enum([...])` later is an ADDITIVE change under
   * annex §8's versioning rule (it removes accepted values from an input, so it
   * needs the ratified list first, but it requires no field rename and no
   * consumer signature change). Do that when Milestone C's corpus settles the
   * membership — not by guessing here.
   *
   * `null` is meaningful and distinct from absent, which is why both are
   * allowed: `null` = detection ran and matched no known platform, absent = no
   * platform information was carried. Kept as `.nullable().optional()` to match
   * the annex's `?:` + `| null` exactly.
   */
  platform: z.string().nullable().optional(),
  /**
   * Which status file this is. Optional per the annex — a single-file upload
   * from a platform with no status split has no role.
   */
  fileRole: sourceFileRoleSchema.optional(),
  declaredBy: sourceFileHintDeclaredBySchema,
});
export type SourceFileHint = z.infer<typeof sourceFileHintSchema>;

// ---------------------------------------------------------------------------
// Annex §4.1b / SPEC §9.3 — the canonical consent vocabulary
// ---------------------------------------------------------------------------

/**
 * SPEC §9.3's canonical vocabulary: "**`opted_in` · `opted_out` ·
 * `needs_review` · `no_data`**".
 *
 * `needs_review` is not a shade of opted-out — it is a *gate*. §9.3:
 * "`needs_review` values require manual confirmation in the wizard's validate
 * step before commit (they never auto-map…). By contract, a `needs_review`
 * value can never reach the write path on an importable row (annex O2)."
 *
 * `no_data` is not a shade of opted-out either. §9.3 rule 4 / INV-2: "**Absent
 * ≠ declined.** `no_data` is a distinct, honest state, never coerced to either
 * pole." §9.3.1 adds that `no_data` must not even write `NEVER_SUBSCRIBED`,
 * "which is a positive assertion we have no basis for" — it leaves the stored
 * field untouched.
 */
export const EMAIL_CONSENT_VALUES = ['opted_in', 'opted_out', 'needs_review', 'no_data'] as const;

export const emailConsentValueSchema = z.enum(EMAIL_CONSENT_VALUES);
export type EmailConsentValue = z.infer<typeof emailConsentValueSchema>;

/**
 * The SMS half of the vocabulary — **three members** as of
 * `decisions.md#D-SMS-OPT-IN-GRANT` (2026-08-14), which ratified OVERVIEW
 * Open Q3.
 *
 * `opted_in` was deliberately absent until that decision: V1 shipped
 * "imports never grant SMS", enforced by making a grant unrepresentable. It is
 * now representable, and the safety that the missing member used to provide has
 * moved to TWO named gates that a grant must pass — see `SmsGrantGate` and
 * `decideImportConsentWrite`.
 *
 * ⚠️ **A VALUE OF `opted_in` HERE IS NOT PERMISSION TO WRITE ONE.** This type
 * says a row's SMS column *claimed* an opt-in. Whether that claim becomes a
 * stored `smsConsent = SUBSCRIBED` is decided downstream, and requires:
 *
 *   1. the claim to come from an **explicit per-row column** — never from a file
 *      role, and never from the attestation default; and
 *   2. a recorded **SMS-specific attestation** on the job record.
 *
 * WHY (2) IS A GATE AND NOT A SOURCE. For email, the commit-screen attestation
 * can itself supply `opted_in` for rows with no consent column (rule 2 of
 * `resolveEmailConsent`) — the CAN-SPAM opt-out default. Giving SMS the same
 * rule would mean a file with NO SMS column silently grants SMS consent to
 * every row in it. That is a worse failure than the one this type used to
 * prevent, so the SMS attestation only ever *permits* a column-sourced grant; it
 * can never *create* one. `resolveEmailConsent` remains email-only.
 *
 * `needs_review` is still deliberately NOT a member. §5.4.2 routes an
 * unresolved SMS value to the triage flag + a warning, and the RESOLVED sms
 * consent that reaches the write path is only ever "grant", "suppress", or "say
 * nothing" — there is no third state for the writer to hold.
 */
export const SMS_CONSENT_VALUES = ['opted_in', 'opted_out', 'no_data'] as const;

export const smsConsentValueSchema = z.enum(SMS_CONSENT_VALUES);
export type SmsConsentValue = z.infer<typeof smsConsentValueSchema>;

/**
 * Annex §4.1b `basis` — WHICH input decided the resolved consent.
 *
 *   - `COLUMN`              — a per-row consent column decided it
 *   - `FILE_ROLE`           — the `SourceFileHint.fileRole` decided it
 *   - `ATTESTATION_DEFAULT` — neither existed; the commit-screen attestation
 *                             default applied (SPEC §9.1)
 *   - `NONE`                — neither existed and the actor declined to attest
 *
 * §4.1b: "`basis` is persisted **per row** so the audit trail survives into the
 * results file — 'we suppressed this person because their old platform's
 * unsubscribed file said so' is answerable months later." SPEC §9.3 says the
 * same thing from the other side: "'why is this person suppressed?' is
 * answerable months later **from the record** rather than reconstructed from
 * the source file."
 *
 * That is the whole justification for it being a stored field rather than a
 * derived one. The source file is not durable — it can be deleted, superseded,
 * or re-exported differently — so a basis recomputed later is a guess. A
 * suppression a nonprofit cannot explain is a suppression they will be tempted
 * to override, which is how an INV-2 violation happens by hand.
 */
export const CONSENT_BASES = ['COLUMN', 'FILE_ROLE', 'ATTESTATION_DEFAULT', 'NONE'] as const;

export const consentBasisSchema = z.enum(CONSENT_BASES);
export type ConsentBasis = z.infer<typeof consentBasisSchema>;

/**
 * Annex §4.1b:
 *
 *     ResolvedConsent {
 *       email: "opted_in" | "opted_out" | "needs_review" | "no_data"
 *       sms:   "opted_out" | "no_data"          // V1 never grants SMS opt-in (§9.1)
 *       basis: "COLUMN" | "FILE_ROLE" | "ATTESTATION_DEFAULT" | "NONE"
 *     }
 *
 * All three required: this is the per-row *resolved* record, produced after
 * resolution has run, so there is no stage at which one of them is legitimately
 * unknown. (`no_data` is how "we know nothing" is expressed — that is a value,
 * not an absence. See INV-2.)
 *
 * One `basis` covers both channels, per the annex. It records which INPUT
 * decided, and the email and SMS resolutions read the same two inputs (column,
 * file role), so a second per-channel basis would carry no information the
 * resolved values don't already imply.
 */
export const resolvedConsentSchema = z.object({
  email: emailConsentValueSchema,
  sms: smsConsentValueSchema,
  basis: consentBasisSchema,
});
export type ResolvedConsent = z.infer<typeof resolvedConsentSchema>;

// ---------------------------------------------------------------------------
// Annex §4.1b — the consent precedence rule (NORMATIVE)
// ---------------------------------------------------------------------------

/**
 * Annex §4.1b projects a `fileRole` onto the canonical email-consent
 * vocabulary. Exported as a named map, not inlined in `resolveEmailConsent`, so
 * a test can assert `CLEANED → no_data` directly — rule 3 is the one a
 * refactor is most likely to quietly break, and it is only observable through
 * the resolver otherwise.
 *
 * ⚠️ **`CLEANED` → `no_data`, NEVER `opted_out`** (annex §4.1b rule 3).
 * SPEC §9.3: Mailchimp `cleaned` "Means bounced, NOT unsubscribed — a common
 * semantic trap." `cleaned → opted_out` records a decline the person never
 * made, and INV-2 makes opt-outs permanent and un-overridable by import, so it
 * is very hard to walk back.
 *
 * ⚠️⚠️ THIS WAS `needs_review` UNTIL PE-2010, AND THE CHANGE IS A DELIBERATE
 * SEAM ESCALATION (annex §8), NOT A TIGHTENING. `needs_review` on a row BLOCKS
 * THE ENTIRE COMMIT: the engine writes the row EXCLUDED with
 * `CONSENT_NEEDS_REVIEW` and raises `needsAttention`, which refuses the job. It
 * is a gate that assumes §9.3's "confirm it in Step 3" triage UI exists — and
 * that UI is UNBUILT (PE-1907). With no way to confirm, a single bounced row in
 * a 5,000-row Mailchimp export refused the whole import and the user's only
 * exits were hand-editing every cell or unmapping the column. A gate nobody can
 * open is a wall.
 *
 * THE ACCEPTED COST, stated plainly: these contacts now import and reach
 * PocketFlows as emailable, including addresses known to have hard-bounced.
 * That is bounded — ESP-side bounce handling suppresses them on the first send
 * — and it was judged strictly better than an unresolvable block. Recorded at
 * `decisions.md#D-DELIVERABILITY-NO-DATA`. If PE-1907 ships a triage UI,
 * restoring `needs_review` here is legitimate; doing so BEFORE that UI exists
 * re-creates the wall.
 *
 * `SUBSCRIBED` → `opted_in`; `UNSUBSCRIBED` → `opted_out` (§9.3's table).
 *
 * `PENDING` → `no_data`: a double-opt-in invitation that was never confirmed.
 * Not `opted_in` — nobody confirmed, so there is no grant. Formerly
 * `needs_review` for the same "a human can resolve this" reasoning as `CLEANED`,
 * and changed with it for the same reason: no human can, yet.
 *
 * `ALL_CONTACTS` and `UNKNOWN` → `no_data`: neither carries consent
 * information. `ALL_CONTACTS` is the explicit "this export is not split by
 * status" case, so it must contribute NOTHING rather than a weak opt-in — an
 * all-contacts file contains the unsubscribed people too, and reading it as
 * `opted_in` would mass-mail exactly the population §9.1 exists to protect.
 *
 * ⚠️ `no_data` HERE MEANS "NOT A SIGNAL", and `resolveEmailConsent` treats it as
 * such: a role projecting to `no_data` is dropped from rule 1's comparison
 * entirely rather than competing at rank 2. Otherwise `ALL_CONTACTS` would
 * shadow rule 2's attestation default (and downgrade a real `opted_in` column
 * to nothing) — see the note in that function's body. This map is therefore the
 * projection, not the participation rule; read both together.
 */
export const EMAIL_CONSENT_BY_FILE_ROLE = {
  SUBSCRIBED: 'opted_in',
  UNSUBSCRIBED: 'opted_out',
  CLEANED: 'no_data',
  PENDING: 'no_data',
  ALL_CONTACTS: 'no_data',
  UNKNOWN: 'no_data',
} as const satisfies Record<SourceFileRole, EmailConsentValue>;

/**
 * The email-consent value a file role implies on its own (annex §4.1b rule 3
 * and §9.3's table). Exported as a function as well as the map above so callers
 * get the total, typed projection without indexing a const object.
 */
export function emailConsentFromFileRole(fileRole: SourceFileRole): EmailConsentValue {
  return EMAIL_CONSENT_BY_FILE_ROLE[fileRole];
}

/**
 * The file roles that carry NO consent information — the roles whose presence
 * must not suppress rule 2's attestation default.
 *
 * ⚠️ THIS IS NOT "THE ROLES THAT PROJECT TO `no_data`", AND CONFLATING THE TWO
 * IS A LIVE BUG (PE-2010). `CLEANED` and `PENDING` also project to `no_data`
 * now, but they are STATEMENTS — "these bounced", "these never confirmed" — and
 * a statement must beat the attestation default, not defer to it. Deriving this
 * set by filtering `EMAIL_CONSENT_BY_FILE_ROLE` for `no_data` would silently
 * re-introduce the defect the moment another role is retoned, which is exactly
 * how this one appeared. It is therefore written out by hand, with the two
 * members named for the reason they qualify.
 *
 *   - `ALL_CONTACTS` — an explicitly unsplit export. It contains subscribed and
 *     unsubscribed people alike, so it asserts nothing about any one row.
 *   - `UNKNOWN`      — the role could not be determined at all.
 *
 * See `resolveEmailConsent` for the consequence of getting this wrong: a
 * hard-bounce file plus a ticked attestation resolving every dead address
 * `opted_in`.
 */
export const NON_SIGNAL_FILE_ROLES: ReadonlySet<SourceFileRole> = new Set([
  'ALL_CONTACTS',
  'UNKNOWN',
] as const satisfies readonly SourceFileRole[]);

/**
 * How restrictive each email-consent value is — LOWER RANK = MORE RESTRICTIVE.
 * `resolveEmailConsent` picks the minimum rank, which is how annex §4.1b rule 1
 * ("the **more restrictive** of (per-row column value, `fileRole`) wins") is
 * implemented as an explicit ordering rather than a pile of `if`s.
 *
 * THE ORDERING, AND WHY IT IS IN THIS ORDER:
 *
 *   0 `opted_out`    — most restrictive, and this rank is the BINDING one.
 *                      §4.1b rule 1: "Declines are sacred; a conflict is never
 *                      resolved toward permission." Rank 0 means opted_out wins
 *                      against every other value from either input, which is
 *                      exactly the annex's worked example: `UNSUBSCRIBED` file
 *                      role + `opted_in` column → `opted_out`.
 *
 *   1 `needs_review` — more restrictive than `no_data`, and this is the
 *                      judgement call in the ordering, so: `needs_review` BLOCKS
 *                      THE COMMIT. §9.3: "a `needs_review` value can never reach
 *                      the write path on an importable row (annex O2)" — a human
 *                      must confirm it in the validate step. `no_data` blocks
 *                      nothing; the row imports, just with no consent basis of
 *                      its own. Gating is strictly stronger than not gating, so
 *                      `needs_review` must outrank `no_data` — otherwise a
 *                      `CLEANED` file (bounced, needs a human) combined with an
 *                      empty consent column would resolve to `no_data` and slip
 *                      past triage silently, losing rule 3's entire protection.
 *
 *   2 `no_data`      — grants nothing but asserts nothing. More restrictive than
 *                      `opted_in` because it confers no permission (INV-2:
 *                      absent ≠ declined, but absent is certainly not consent).
 *                      It must NOT be more restrictive than `opted_out` or
 *                      `needs_review`: coercing a real signal to `no_data`
 *                      because the other input was empty would throw away the
 *                      signal — which is why the resolver never lets an ABSENT
 *                      input participate as `no_data` in the comparison (see
 *                      the function body).
 *
 *   3 `opted_in`     — least restrictive. Only wins when it is the only signal,
 *                      or when both inputs say it.
 *
 * The binding requirements this ordering satisfies, restated: a conflict never
 * resolves toward MORE permission, and `opted_out` always wins.
 */
export const EMAIL_CONSENT_RESTRICTIVENESS = {
  opted_out: 0,
  needs_review: 1,
  no_data: 2,
  opted_in: 3,
} as const satisfies Record<EmailConsentValue, number>;

/**
 * The attestation default (SPEC §9.1). Only two outcomes exist:
 *
 *   - attested   → `opted_in`  — "by default, imported contacts are treated as
 *                  email-consented — the nonprofit attests permission at the
 *                  commit screen"
 *   - declined   → `no_data`   — "Attestation **declined** → the consent field
 *                  is left unset — never defaulted to true."
 *
 * There is deliberately no `opted_out` here: declining to attest is not the
 * nonprofit declaring their whole list unsubscribed. §9.1's decline path stores
 * the contacts and lets the shipped PocketFlows suppression behavior keep them
 * out of sends until a basis exists — it does not manufacture opt-outs.
 */
export const ATTESTATION_DEFAULT_VALUES = ['opted_in', 'no_data'] as const;

export const attestationDefaultSchema = z.enum(ATTESTATION_DEFAULT_VALUES);
export type AttestationDefault = z.infer<typeof attestationDefaultSchema>;

/**
 * **THE CONSENT PRECEDENCE RULE, EXECUTABLE.**
 *
 * Annex §4.1b calls this rule *normative* and notes it is "stated here because
 * it was previously stated nowhere". A normative rule that lives only in prose
 * gets re-derived — differently — by the validator, the write path, and the
 * results-file writer. It is a function here so all three call the same one and
 * a test can pin every branch.
 *
 * THE FOUR RULES (annex §4.1b, mirrored verbatim in SPEC §9.3), in order:
 *
 *  1. The **more restrictive** of (per-row column value, `fileRole`) wins. An
 *     `UNSUBSCRIBED` file role and an `opted_in` column resolve to
 *     `opted_out`. "Declines are sacred; a conflict is never resolved toward
 *     permission." → implemented as the minimum of
 *     `EMAIL_CONSENT_RESTRICTIVENESS` over the inputs that are PRESENT.
 *  2. Absent both, the attestation default applies (`basis:
 *     ATTESTATION_DEFAULT`), or `no_data` if the actor declined to attest
 *     (`basis: NONE` — a decline establishes no basis; see the function body).
 *  3. `CLEANED` → `needs_review`, never `opted_out` — it means bounced (SPEC
 *     §9.3's documented semantic trap). → lives in
 *     `EMAIL_CONSENT_BY_FILE_ROLE`, so it is one lookup away from a test.
 *  4. **Absent ≠ declined** (INV-2). `no_data` is a distinct, honest state and
 *     is never coerced to `opted_out`.
 *
 * ⚠️ ABSENT INPUTS DO NOT PARTICIPATE IN RULE 1. Only inputs actually supplied
 * are compared. This matters because `no_data` is a legal *value*, so
 * "missing" and "explicitly no_data" must not collapse: if an absent column
 * were treated as `no_data` (rank 2), then an `UNSUBSCRIBED` file role (rank 0)
 * would still win — fine — but a `SUBSCRIBED` file role (`opted_in`, rank 3)
 * would lose to the phantom `no_data` and a legitimately subscribed file would
 * resolve to no consent at all. Worse, with both inputs absent the minimum
 * would be `no_data` and rule 2's attestation default would never run.
 *
 * ⚠️ WHY `basis` IS RETURNED, NOT INFERRED BY THE CALLER. Two different inputs
 * can produce the same resolved value, and only this function knows which one
 * decided. Tie-breaking is specified: when the column is the decider, or ties
 * with the role, `basis` is `COLUMN` (the per-row signal is the more specific
 * statement about this person, and it is the one a reviewer can check against
 * the source row); `basis` is `FILE_ROLE` only when the role is STRICTLY more
 * restrictive than the column — i.e. exactly the conflict case rule 1 exists to
 * arbitrate, which is also the case the audit trail most needs to explain
 * ("their old platform's unsubscribed file said so").
 *
 * SMS is NOT resolved here. `SmsConsentValue` has no `opted_in`, so SMS cannot
 * be projected from a file role or from the attestation default at all (§9.1:
 * V1 never grants SMS opt-in); the only SMS input is an explicit opt-out
 * signal. Resolving it through this shared ordering would invite exactly the
 * coercion the two-member type forbids.
 *
 * @param input.columnValue Consent mapped from a per-row consent column (§9.3).
 *   Omit when the file has no consent column, or the column is empty for this
 *   row — omitted is NOT the same as `'no_data'` (see above).
 * @param input.fileRole The `SourceFileHint.fileRole` for the file this row came
 *   from (annex §4.1b). Omit when the upload carried no role.
 * @param input.attestationDefault The commit-screen attestation outcome, as a
 *   consent value: `'opted_in'` when attested, `'no_data'` when declined
 *   (§9.1). Omit when no attestation decision has been recorded yet. `'no_data'`
 *   and omitted resolve identically (`no_data` / `basis: NONE`) — both mean no
 *   consent basis was established.
 * @returns The resolved email consent and the basis that decided it — the
 *   `email` and `basis` halves of `ResolvedConsent`.
 */
export function resolveEmailConsent(input: {
  columnValue?: EmailConsentValue;
  fileRole?: SourceFileRole;
  attestationDefault?: AttestationDefault;
}): { email: EmailConsentValue; basis: ConsentBasis } {
  const { columnValue, fileRole, attestationDefault } = input;

  // Rule 3 lives in the projection map, so the role's contribution is already
  // CLEANED-safe before it reaches the comparison below.
  //
  // ⚠️ A role that projects to `no_data` is NOT A SIGNAL and must not
  // participate. `ALL_CONTACTS` and `UNKNOWN` both project to `no_data` because
  // they carry no consent information — that is the absence of a statement, not
  // a statement of absence. Letting them through would make them shadow rule
  // 2: an `ALL_CONTACTS` file (the single most common shape — one unsplit
  // export) plus a confirmed attestation would resolve to `no_data`, i.e.
  // attesting permission would silently do nothing for every row. Collapsing
  // them to `undefined` here is what makes "falling back to the attestation
  // default" actually happen, and it is the same reasoning that keeps an absent
  // column from participating as a phantom `no_data`.
  //
  // This does NOT weaken rule 1: `no_data` is rank 2, so a role projecting to
  // `no_data` could only ever have "won" against `opted_in` (rank 3) — that is,
  // its only possible effect was to DOWNGRADE a real column signal to nothing.
  //
  // ⚠️ THE TEST IS ON THE ROLE, NOT ON ITS PROJECTION — and that distinction
  // became load-bearing in PE-2010. `NON_SIGNAL_FILE_ROLES` names the roles that
  // carry no consent information at all. Before PE-2010 the two formulations
  // were equivalent, because `ALL_CONTACTS`/`UNKNOWN` were the only roles
  // projecting to `no_data`, so filtering on the projection was a harmless
  // shorthand. Retoning `CLEANED`/`PENDING` to `no_data` broke that equivalence:
  // a projection test would have swept them in here and dropped them from the
  // comparison, so a hard-bounce export uploaded WITH the email attestation
  // ticked would fall through to rule 2 and resolve every dead address
  // `opted_in`. That is a positive consent claim manufactured from a file whose
  // entire meaning is "these addresses bounced".
  //
  // The two cases are genuinely different and must stay distinguishable:
  //   - `ALL_CONTACTS` / `UNKNOWN` — the file says NOTHING about consent, so the
  //     attestation default is the honest fallback.
  //   - `CLEANED` / `PENDING`      — the file makes a STATEMENT that happens to
  //     resolve `no_data`. It is still a statement, and it must suppress the
  //     attestation default rather than defer to it.
  const roleValue =
    fileRole === undefined || NON_SIGNAL_FILE_ROLES.has(fileRole)
      ? undefined
      : emailConsentFromFileRole(fileRole);

  // Rule 1 — the more restrictive of the inputs that are PRESENT wins.
  if (columnValue !== undefined && roleValue !== undefined) {
    const roleIsStrictlyMoreRestrictive =
      EMAIL_CONSENT_RESTRICTIVENESS[roleValue] < EMAIL_CONSENT_RESTRICTIVENESS[columnValue];

    return roleIsStrictlyMoreRestrictive
      ? { email: roleValue, basis: 'FILE_ROLE' }
      : // Column decided, or tied — the per-row signal is the more specific
        // statement about this person and the one a reviewer can verify.
        { email: columnValue, basis: 'COLUMN' };
  }

  if (columnValue !== undefined) {
    return { email: columnValue, basis: 'COLUMN' };
  }

  if (roleValue !== undefined) {
    return { email: roleValue, basis: 'FILE_ROLE' };
  }

  // Rule 2 — absent both, the attestation default applies…
  if (attestationDefault === 'opted_in') {
    return { email: 'opted_in', basis: 'ATTESTATION_DEFAULT' };
  }

  // …or `no_data` with `basis: NONE` when the actor declined to attest, or when
  // no attestation decision has been recorded at all. Both collapse here on
  // purpose: `NONE` means "no consent basis was established", and a DECLINE
  // establishes none — §9.1's declined path "left unset — never defaulted to
  // true", and §9.3 rule 2's "absent attestation, `no_data`". Returning
  // `ATTESTATION_DEFAULT` for a decline would claim the attestation was the
  // basis for a consent state it explicitly refused to grant, which is the one
  // reading of the audit trail that is actively misleading. (The distinction
  // between declined and not-yet-asked is not lost — it lives on the job
  // record's `consentAttestation.attested` per §3.1/§9.2, which is where it
  // belongs; this per-row basis records only whether a basis exists.)
  //
  // Rule 4: `no_data` here is an honest "we know nothing", NOT an opt-out.
  return { email: 'no_data', basis: 'NONE' };
}
